import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Star, 
  TrendingUp, 
  TrendingDown,
  Trophy,
  Clock,
  Target,
  ChevronRight,
  Trash2,
  Loader2,
  Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { API } from '../config/api';

const formatTime = (seconds) => {
  if (!seconds || seconds === 0) return '--';
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(3);
  return mins > 0 ? `${mins}:${secs.padStart(6, '0')}` : `${secs}s`;
};

const FavoritesDashboard = ({ userId, onSelectSession }) => {
  const [favorites, setFavorites] = useState([]);
  const [evolution, setEvolution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState(null);

  useEffect(() => {
    if (userId) {
      loadFavorites();
      loadEvolution();
    }
  }, [userId]);

  const loadFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites?user_id=${userId}`);
      setFavorites(response.data);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const loadEvolution = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/favorites/evolution?user_id=${userId}`);
      setEvolution(response.data);
      if (response.data.length > 0 && !selectedDriver) {
        setSelectedDriver(response.data[0].driver_name);
      }
    } catch (error) {
      console.error('Error loading evolution:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (driverName) => {
    if (!window.confirm(`¿Quitar a ${driverName} de favoritos?`)) return;
    
    try {
      await axios.delete(`${API}/favorites/by-name/${encodeURIComponent(driverName)}?user_id=${userId}`);
      toast.success(`${driverName} eliminado de favoritos`);
      loadFavorites();
      loadEvolution();
    } catch (error) {
      toast.error('Error al eliminar favorito');
    }
  };

  const getSelectedEvolution = () => {
    return evolution.find(e => e.driver_name === selectedDriver);
  };

  const prepareChartData = () => {
    const selected = getSelectedEvolution();
    if (!selected) return [];
    
    return selected.sessions.map((session, idx) => ({
      name: session.session_name.split(' - ').slice(-1)[0] || `Sesión ${idx + 1}`,
      fullName: session.session_name,
      date: session.session_date,
      bestLap: session.best_lap,
      avgLap: session.avg_lap,
      position: session.position
    }));
  };

  if (loading) {
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (favorites.length === 0) {
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="py-12 text-center">
          <Star className="w-16 h-16 mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400 text-lg mb-2">No tenés pilotos favoritos</p>
          <p className="text-sm text-gray-500">
            Marcá pilotos con ⭐ desde el análisis de carreras para seguir su evolución
          </p>
        </CardContent>
      </Card>
    );
  }

  const selectedEvolution = getSelectedEvolution();
  const chartData = prepareChartData();

  return (
    <div className="space-y-6">
      {/* Favorites List */}
      <Card className="bg-neutral-900 border-red-900/30">
        <CardHeader>
          <CardTitle className="text-xl text-white flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            Mis Pilotos Favoritos
          </CardTitle>
          <CardDescription className="text-gray-400">
            Seguí la evolución de tus pilotos favoritos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {evolution.map((driver) => (
              <button
                key={driver.driver_name}
                onClick={() => setSelectedDriver(driver.driver_name)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                  selectedDriver === driver.driver_name
                    ? 'bg-red-600 border-red-500 text-white'
                    : 'bg-neutral-800/50 border-red-900/30 text-gray-300 hover:border-red-600'
                }`}
              >
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <span className="font-medium">{driver.driver_name}</span>
                <Badge variant="secondary" className="bg-neutral-700 text-xs">
                  {driver.total_sessions} sesiones
                </Badge>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFavorite(driver.driver_name);
                  }}
                  className="ml-1 p-1 hover:bg-red-700 rounded transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Evolution Chart */}
      {selectedEvolution && chartData.length > 0 && (
        <Card className="bg-neutral-900 border-red-900/30">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Evolución de {selectedDriver}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Stats Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-neutral-800/50 rounded-lg p-4 text-center">
                <Trophy className="w-6 h-6 mx-auto text-yellow-500 mb-2" />
                <p className="text-2xl font-bold text-white">{formatTime(selectedEvolution.best_ever_lap)}</p>
                <p className="text-xs text-gray-400">Mejor Tiempo</p>
              </div>
              <div className="bg-neutral-800/50 rounded-lg p-4 text-center">
                <Clock className="w-6 h-6 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold text-white">{formatTime(selectedEvolution.avg_best_lap)}</p>
                <p className="text-xs text-gray-400">Promedio Mejores</p>
              </div>
              <div className="bg-neutral-800/50 rounded-lg p-4 text-center">
                <Target className="w-6 h-6 mx-auto text-red-500 mb-2" />
                <p className="text-2xl font-bold text-white">{selectedEvolution.total_sessions}</p>
                <p className="text-xs text-gray-400">Sesiones</p>
              </div>
            </div>

            {/* Chart */}
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#666" 
                    tick={{ fill: '#999', fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    stroke="#666" 
                    tick={{ fill: '#999' }}
                    domain={['dataMin - 1', 'dataMax + 1']}
                    tickFormatter={(val) => formatTime(val)}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1a1a1a', 
                      border: '1px solid #333',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(label, payload) => {
                      if (payload && payload[0]) {
                        return `${payload[0].payload.fullName} (${payload[0].payload.date})`;
                      }
                      return label;
                    }}
                    formatter={(value, name) => [formatTime(value), name === 'bestLap' ? 'Mejor Vuelta' : 'Promedio']}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="bestLap" 
                    name="Mejor Vuelta"
                    stroke="#22c55e" 
                    strokeWidth={2}
                    dot={{ fill: '#22c55e', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="avgLap" 
                    name="Promedio"
                    stroke="#f59e0b" 
                    strokeWidth={2}
                    dot={{ fill: '#f59e0b', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sessions List */}
      {selectedEvolution && (
        <Card className="bg-neutral-900 border-red-900/30">
          <CardHeader>
            <CardTitle className="text-lg text-white">
              Historial de Sesiones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {selectedEvolution.sessions.map((session, idx) => {
                const prevSession = idx > 0 ? selectedEvolution.sessions[idx - 1] : null;
                const improvement = prevSession ? prevSession.best_lap - session.best_lap : 0;
                
                return (
                  <div 
                    key={session.session_id}
                    className="flex items-center gap-4 p-3 bg-neutral-800/30 rounded-lg hover:bg-neutral-800/50 transition-colors cursor-pointer"
                    onClick={() => onSelectSession && onSelectSession(session.session_id)}
                  >
                    <div className="w-8 h-8 bg-neutral-700 rounded-full flex items-center justify-center text-sm font-bold text-white">
                      {session.position}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{session.session_name}</p>
                      <p className="text-xs text-gray-500">{session.session_date} • {session.lap_count} vueltas</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-mono">{formatTime(session.best_lap)}</p>
                      {improvement !== 0 && (
                        <p className={`text-xs flex items-center justify-end gap-1 ${improvement > 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {improvement > 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                          {improvement > 0 ? '-' : '+'}{Math.abs(improvement).toFixed(3)}s
                        </p>
                      )}
                    </div>
                    <div className="text-gray-500">
                      <Badge variant="outline" className="text-xs">
                        P{session.position}/{session.total_drivers}
                      </Badge>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FavoritesDashboard;
